import React, { useState, useEffect } from 'react';
import { Container, Form, Button, Alert, Row, Col } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import { useCookies } from 'react-cookie';
import { useForm, SubmitHandler } from 'react-hook-form';
import toast from 'react-hot-toast';
import axiosInstance from 'helpers/api';
import NavigationHeader from './components/NavigationHeader';
import FooterWithPolicies from './components/FooterWithPolicies';
import { BsChatDots, BsEnvelope, BsLock, BsEye, BsEyeSlash, BsArrowRight, BsGoogle, BsMicrosoft } from 'react-icons/bs';
import './styles/chat-realestate.css';
import { initializePermissions } from 'utils/databasePermissions';

type SignInFormInputs = {
  email: string;
  password: string;
  rememberMe: boolean;
};

const SignIn: React.FC = () => {
  const navigate = useNavigate();
  const [cookie, setCookie] = useCookies(['isLoggedInKIQ']);
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<SignInFormInputs>();

  const onSubmit: SubmitHandler<SignInFormInputs> = async (data) => {
    try {
      setIsLoading(true);
      
      // Check if this is a new user or returning user
      const response = await axiosInstance.post('/auth/login', {
        email: data.email,
        password: data.password,
        source: 'chat.realestate', // Track source for lead attribution
      });
      
      if (response.data.data?.token) {
        localStorage.setItem('auth_token', response.data.data.token);
      }
      
      if (response.data.data?.user) {
        localStorage.setItem('user_info', JSON.stringify(response.data.data.user));
        
        // Track as lead if this is a public user
        if (response.data.data.user.user_role === 'PUBLIC') {
          try {
            await axiosInstance.post('/leads/track-public-access', {
              email: data.email,
              source: 'chat.realestate',
              action: 'signin',
              timestamp: new Date().toISOString()
            });
          } catch (leadError) {
            console.error('Lead tracking failed:', leadError);
            // Don't block signin if lead tracking fails
          }
        }
      }
      
      setCookie('isLoggedInKIQ', true);
      
      // Initialize database permissions after login
      await initializePermissions();
      
      // Redirect based on user type
      const isPublicUser = response.data.data?.user?.user_role === 'PUBLIC';
      if (isPublicUser) {
        toast.success('Welcome! Let\'s find your dream home.');
        setTimeout(() => {
          navigate('/search');
        }, 1000);
      } else {
        toast.success('Welcome back! Redirecting to property search...');
        setTimeout(() => {
          navigate('/search');
        }, 1000);
      }
      
    } catch (error: any) {
      if (error?.response?.status === 404) {
        // User not found - suggest signup
        toast.error('No account found. Please sign up to continue.');
        setTimeout(() => {
          navigate('/sign-up');
        }, 2000);
      } else {
        toast.error(error?.response?.data?.message || 'Invalid email or password');
      }
      setIsLoading(false);
    }
  };

  const handleSocialLogin = (provider: 'google' | 'microsoft') => {
    toast(`${provider} login coming soon!`);
  };


  return (
    <div className="chat-realestate-wrapper">
      <NavigationHeader />
      
      <div className="signin-container">
        <div className="signin-content">
            <div className="signin-card">
              {/* Header */}
              <div className="text-center mb-5">
                <div className="signin-icon-wrapper mb-4">
                  <div className="signin-icon">
                    <BsChatDots size={32} />
                  </div>
                </div>
                <h2 className="signin-title">Welcome Back</h2>
                <p className="signin-subtitle">
                  Sign in to access your AI-powered property search
                </p>
              </div>

              {/* Social Login Options */}
              <div className="social-login-section mb-4">
                <button
                  type="button"
                  className="social-login-btn google-btn w-100 mb-3"
                  onClick={() => handleSocialLogin('google')}
                  style={{ 
                    background: '#ffffff', 
                    color: '#000000', 
                    border: '2px solid #000000',
                    fontWeight: 700 
                  }}
                >
                  <BsGoogle />
                  <span>Continue with Google</span>
                </button>
                <button
                  type="button"
                  className="social-login-btn microsoft-btn w-100"
                  onClick={() => handleSocialLogin('microsoft')}
                  style={{ 
                    background: '#ffffff', 
                    color: '#000000', 
                    border: '2px solid #000000',
                    fontWeight: 700 
                  }}
                >
                  <BsMicrosoft />
                  <span>Continue with Microsoft</span>
                </button>
              </div>

              {/* Divider */}
              <div className="auth-divider">
                <span>or sign in with email</span>
              </div>

              {/* Sign In Form */}
              <Form onSubmit={handleSubmit(onSubmit)}>
                <Form.Group className="mb-4">
                  <Form.Label className="form-label-enhanced">
                    <BsEnvelope className="me-2" />
                    Email Address
                  </Form.Label>
                  <Form.Control
                    type="email"
                    placeholder="your@email.com"
                    className="form-control-enhanced"
                    {...register('email', {
                      required: 'Email is required',
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: 'Invalid email address',
                      },
                    })}
                    isInvalid={!!errors.email}
                  />
                  {errors.email && (
                    <Form.Control.Feedback type="invalid">
                      {errors.email.message}
                    </Form.Control.Feedback>
                  )}
                </Form.Group>

                <Form.Group className="mb-4">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <Form.Label className="form-label-enhanced mb-0">
                      <BsLock className="me-2" />
                      Password
                    </Form.Label>
                    <Link to="/forgot-password" className="forgot-password-link">
                      Forgot password?
                    </Link>
                  </div>
                  <div className="password-input-wrapper">
                    <Form.Control
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Enter your password"
                      className="form-control-enhanced"
                      {...register('password', {
                        required: 'Password is required',
                      })}
                      isInvalid={!!errors.password}
                    />
                    <button
                      type="button"
                      className="password-toggle-btn"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <BsEyeSlash /> : <BsEye />}
                    </button>
                  </div>
                  {errors.password && (
                    <Form.Control.Feedback type="invalid" className="d-block">
                      {errors.password.message}
                    </Form.Control.Feedback>
                  )}
                </Form.Group>

                <Form.Group className="mb-4">
                  <Form.Check
                    type="checkbox"
                    id="rememberMe"
                    label="Remember me for 30 days"
                    className="remember-me-check"
                    {...register('rememberMe')}
                  />
                </Form.Group>

                <Button
                  type="submit"
                  variant="primary"
                  className="signin-submit-btn w-100 mb-4"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" />
                      Signing in...
                    </>
                  ) : (
                    <>
                      Sign In
                      <BsArrowRight className="ms-2" />
                    </>
                  )}
                </Button>
              </Form>

              {/* Sign Up Link */}
              <div className="text-center">
                <p className="signup-prompt">
                  Don't have an account?{' '}
                  <Link to="/sign-up" className="signup-link">
                    Sign up for free
                  </Link>
                </p>
              </div>

            </div>
        </div>
      </div>

      <FooterWithPolicies />
    </div>
  );
};

export default SignIn;